import { map, Observable } from 'rxjs';
import { Inject, Injectable, Injector, Signal } from '@angular/core';

/**
 * libs
 */
import { IClientOptions } from 'mqtt';

/**
 * interfaces
*/
import { IMqttConnectionOptions, IMqttAuthProviderSnapshot, IProfileConfig, TopicFilter, IConnectionMessage, ITopicOptions } from './interfaces';

/**
 * utilities
 */
import { MqttUtilities } from './utilities';

/**
 * models
 */
import { ConnectionHandler, ProfileRegistry, TopicHandler, TopicRegistry } from './models';

/**
 * services/tokens
 */
import { MqttCoreService } from './mqtt-core.service';
import { MQTT_OPTIONS, MQTT_OPTIONS_AUTH_PROVIDER, MQTT_PROFILES } from './providers-tokens/providers';

/**
 * Main Facade Service for MQTT Communication.
 * @class MqttClientService
 * @description Provides a high-level API for interacting with the MQTT broker.
 * It orchestrates connection management, topic subscriptions, and profile handling.
 * Developers should inject this service to use MQTT features in their components and services.
 * 
 * @example
 * ```typescript
 * // Inject in component
 * constructor(private mqttClient: MqttClientService) {}
 * 
 * // Subscribe to a topic
 * this.mqttClient.observeTopic('my/topic').subscribe(data => console.log(data));
 * 
 * // Use with Signals
 * const dataSignal = this.mqttClient.topicSignal('my/topic', []);
 * ```
 */
@Injectable()
export class MqttClientService {

  /**
   * The connection handler instance responsible for the raw socket connection.
   * @private
   */
  private readonly _connectionHandler!: ConnectionHandler;

  /**
   * The registry managing feature-based profiles.
   * @private
   */
  private readonly profileRegistry!: ProfileRegistry;

  /**
   * The registry managing individual topic subscriptions.
   * @private
   */
  private readonly topicRegistry!: TopicRegistry;

  /**
   * The MQTT connection options used for this client.
   * @public
   */
  public get options(): IMqttConnectionOptions {
    return this._options;
  }

  /**
   * Creates an instance of MqttClientService.
   * @constructor
   * @param {IMqttConnectionOptions} _options - Configuration options for the MQTT connection (URL, etc.).
   * @param {IMqttAuthProviderSnapshot} _authProvider - Authentication provider for dynamic token management.
   * @param {IProfileConfig[]} _profiles - List of profile configurations to pre-register.
   * @param {MqttCoreService} _mqttCoreService - Internal core service for tracking client instances.
   */
  constructor(
    @Inject(MQTT_OPTIONS)
    private _options: IMqttConnectionOptions,
    @Inject(MQTT_OPTIONS_AUTH_PROVIDER)
    private _authProvider: IMqttAuthProviderSnapshot,
    @Inject(MQTT_PROFILES)
    private _profiles: IProfileConfig[],
    private _mqttCoreService: MqttCoreService,
  ) {

    // Add the MqttClientService instance to the collection
    this._addToCollection();

    // ES6 Deconstruction
    const { url, connectionId, topicPrefix } = this._options;

    // create a new connection handler
    this._connectionHandler = new ConnectionHandler(url, connectionId!, this._options, this._authProvider);

    // create a new topic registry
    this.topicRegistry = new TopicRegistry(this._connectionHandler.mqttClient);

    // create a new profile registry & store profiles that is come from the providers
    this.profileRegistry = new ProfileRegistry(connectionId!, this.topicRegistry, this._profiles);

    // subscribe to the message observable
    this._connectionHandler
          .message$
          .subscribe(message => {
            this._handleMessage(message, this.topicRegistry)
          });

  }

  /**
   * Add the MqttClientService instance to the collection.
   * @private
   */
  private _addToCollection(): void {
    this._mqttCoreService.addMqttClient(this);
  }

  /** 
   * Internal message handler that routes incoming MQTT messages to the appropriate TopicHandler.
   * @private
   * @param {IConnectionMessage} message - The incoming message object containing topic, payload, and packet.
   * @param {TopicRegistry} topicRegistry - The registry to look up the handler for the topic.
   */
  private _handleMessage(message: IConnectionMessage, topicRegistry: TopicRegistry): void {

    // ES6 Deconstruction
    const { topic, payload, packet } = message || {};

    // Global Validation
    if (!topic) {
      return;
    }

    try {

      // Convert the payload to a string and parse it as JSON
      const _payloadString = payload?.toString() ?? '';
      const _payloadParsed = JSON.parse(_payloadString);

      // Get every handler whose filter covers this topic. Matched rather than
      // looked up by key: a handler subscribed with an array of topics, or a
      // wildcard, is registered under that filter and never under the concrete
      // topic a message actually arrives on.
      const topicHandlers = topicRegistry.matchingTopics(topic);

      // Set the payload on each of them — overlapping filters are legitimate.
      topicHandlers.forEach(topicHandler => topicHandler.setPayload(_payloadParsed, packet));

    } 
    catch (error) {
      // Log the error
      console.error(error);
    }

  }

  /**
   * Retrieves or creates a TopicHandler for the specified topic.
   * @public
   * @template T - The type of the data expected from this topic.
   * @param {TopicFilter} topic - The topic string or filter to subscribe to.
   * @param {Partial<ITopicOptions<T>>} [options] - The subscription options.
   * @param {string} [topicPrefix] - The topic prefix to apply to the topic.
   * @returns {TopicHandler<T>} The handler instance for the topic.
   */
  public topic<T = any>(topic: TopicFilter, options?: Partial<ITopicOptions<T>>, topicPrefix?: string): TopicHandler<T> {

    const _builtTopic = MqttUtilities.buildTopicFilter(topic, topicPrefix ?? this._options.topicPrefix);

    const topicHandler = this.topicRegistry.createOrGetTopic<T>(_builtTopic, options);

    if(!this._connectionHandler.connectedAtLeastOnce) {
      this.connect();
    }

    return topicHandler

  }

  /**
   * Returns an Observable stream of data for the specified topic.
   * Use this for RxJS-based reactive flows.
   * @public
   * @template T - The type of the data expected.
   * @param {TopicFilter} topic - The topic to observe.
   * @param {Partial<ITopicOptions<T>>} [options] - The subscription options.
   * @returns {Observable<T>} A hot observable that emits new data when messages arrive.
   */
  public observeTopic<T = any>(topic: TopicFilter, options?: Partial<ITopicOptions<T>>): Observable<T> {
    return this.topic<T>(topic, options).observeTopic;
  }

  /**
   * Returns an Angular Signal for the specified topic data.
   * Use this for template binding and signal-based reactivity.
   * @public
   * @template T - The type of the data expected.
   * @param {TopicFilter} topic - The topic to observe.
   * @param {Partial<ITopicOptions<T>>} [options] - Optional options for the topic.
   * @returns {Signal<T>} A signal containing the latest topic data.
   */
  public topicSignal<T = any>(topic: TopicFilter, options?: Partial<ITopicOptions<T>>, injector?: Injector): Signal<T> {
    return this.topic<T>(topic, options).topicSignal(options?.initialValue, injector);
  }

  /**
   * Destroys the handler for a topic and releases its broker subscription.
   * @public
   * @param {TopicFilter} topic - The topic string or filter to tear down.
   * @param {string} [topicPrefix] - The topic prefix to apply, matching `topic()`.
   * @description
   * For releasing a subscription earlier than its consumers would — a page
   * swapping one set of topics for another. Ordinary teardown needs no call
   * here: dropping the last consumer starts the `resetOnRefCountZero` timer,
   * which unsubscribes and clears the registry on its own.
   *
   * Does nothing when the topic is not registered.
   */
  public destroyTopic(topic: TopicFilter, topicPrefix?: string): void {

    const _builtTopic = MqttUtilities.buildTopicFilter(topic, topicPrefix ?? this._options.topicPrefix);

    // The handler removes itself from the registry through `shutdown$`.
    this.topicRegistry.topic(_builtTopic)?.destroy();

  }

  /**
   * Initiates the connection to the MQTT broker.
   * @public
   */
  public connect(): void {
    this._connectionHandler.connect();
  }

  /**
   * Gracefully disconnects from the MQTT broker.
   * @public
   * @param {boolean} [force] - If true, forces the disconnection without waiting for cleanup.
   */
  public disconnect(force?: boolean): void {
    this._connectionHandler.disconnect(force);
  }

  /**
   * Forcefully terminates the connection immediately.
   * @public
   */
  public forceDisconnect(): void {
    this._connectionHandler.forceDisconnect();
  }

  /**
   * Reconnects to the broker, optionally with new store options.
   * Useful if connection parameters need to be updated manually.
   * @public
   * @param {Pick<IClientOptions, 'incomingStore' | 'outgoingStore'>} [opts] - Store options for reconnection.
   */
  public reconnect(opts?: Pick<IClientOptions, 'incomingStore' | 'outgoingStore'>): void {
    this._connectionHandler.reconnect(opts);
  }

  /**
   * Removes all event listeners from the underlying client.
   * @public
   */
  public removeAllListeners(): void {
    this._connectionHandler.removeAllListeners();
  }

  /**
   * Destroys the client, registries, and connection handler.
   * Should be called when the service is no longer needed to prevent memory leaks.
   * @public
   */
  public destroy(): void {
    this.topicRegistry.destroy();
    this.profileRegistry.destroy();
    this._connectionHandler.destroy();
  }

}
